import { useEffect, useState } from "react";
import { ModalShell } from "@/components/ui/modal-shell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { BirthDateInput } from "@/components/ui/birth-date-input";
import { useCustomerAuth, type CustomerCodeChallenge, type CustomerProfile } from "@/hooks/use-customer-auth";
import { CustomerCodeStep } from "@/components/CustomerCodeStep";
import { capitalizeWords } from "@/lib/text";
import { Loader2 } from "lucide-react";
import { CODIGO_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";

interface CustomerLoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthenticated?: (customer: CustomerProfile) => void;
  title?: string;
  description?: string;
  dismissible?: boolean;
  askBirthDate?: boolean;
  birthDateRequired?: boolean;
  /** Loja do link. Com pontos ligados, quem já é cliente dela confirma o número com um código. */
  storeSlug?: string;
  loyaltyEnabled?: boolean;
}

export function CustomerLoginModal({
  open,
  onOpenChange,
  onAuthenticated,
  title = "Entrar com WhatsApp",
  description,
  dismissible = true,
  askBirthDate = false,
  birthDateRequired = false,
  storeSlug,
  loyaltyEnabled = false,
}: CustomerLoginModalProps) {
  const { login, verifyCode } = useCustomerAuth();
  const [challenge, setChallenge] = useState<CustomerCodeChallenge | null>(null);
  const subtitle =
    description ??
    (loyaltyEnabled
      ? "Se você já é cliente da loja, mandamos um código no seu WhatsApp."
      : "Confirmamos sua identidade pelo número de WhatsApp. Não enviamos código, só validamos se o número existe.");
  const [whatsapp, setWhatsapp] = useState("");
  const [ddi, setDdi] = useState("55");
  const [firstName, setFirstName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // O «Entrar» só manda código para quem já é cliente de loja com pontos; o resto volta logo. Por isso o aviso dos 30 s
  // só aparece se a espera passar de 3 s.
  const [demorou, setDemorou] = useState(false);

  useEffect(() => {
    setDemorou(false);
    if (!submitting || challenge) return;
    const t = setTimeout(() => setDemorou(true), 3000);
    return () => clearTimeout(t);
  }, [submitting, challenge]);

  // Zera ao ABRIR (não ao fechar): zerar no fechamento redesenhava o 1º passo durante a animação de saída.
  useEffect(() => {
    if (open) {
      setWhatsapp("");
      setDdi("55");
      setFirstName("");
      setBirthDate("");
      setNeedsName(false);
      setError(null);
      setSubmitting(false);
      setChallenge(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    const digits = whatsapp.replace(/\D/g, "");
    if (!digits) {
      setError("Informe seu número de WhatsApp.");
      return;
    }
    if (needsName && !firstName.trim()) {
      setError("Informe seu nome.");
      return;
    }
    if (needsName && askBirthDate && birthDateRequired && !birthDate) {
      setError("Informe sua data de nascimento.");
      return;
    }
    setSubmitting(true);
    const result = await login({
      whatsappNumber: digits,
      ddi,
      firstName: needsName ? firstName.trim() : undefined,
      birthDate: needsName && askBirthDate && birthDate ? birthDate : undefined,
      ...(storeSlug ? { storeSlug } : {}),
    });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (result.needsCode) {
      setChallenge(result.needsCode);
      return;
    }
    if (result.needsName) {
      setNeedsName(true);
      return;
    }
    if (result.customer) {
      onAuthenticated?.(result.customer);
      onOpenChange(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !dismissible) return;
    onOpenChange(next);
  };

  return (
    <ModalShell
      open={open}
      onOpenChange={handleOpenChange}
      title={challenge ? "Confirme que é você" : title}
      subtitle={challenge ? undefined : subtitle}
      size="sm"
      dismissable={dismissible}
    >
      {challenge ? (
        <CustomerCodeStep
          maskedNumber={challenge.maskedNumber}
          submitting={submitting}
          error={error}
          onResend={() => void handleSubmit()}
          onSubmit={async (code) => {
            setSubmitting(true);
            setError(null);
            const r = await verifyCode(challenge.requestId, code);
            setSubmitting(false);
            if (r.error) {
              setError(r.error);
              return;
            }
            if (r.customer) {
              onAuthenticated?.(r.customer);
              onOpenChange(false);
            }
          }}
        />
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-whatsapp">WhatsApp</Label>
            <WhatsAppInput
              id="customer-whatsapp"
              value={whatsapp}
              onChange={setWhatsapp}
              ddi={ddi}
              onDdiChange={setDdi}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !needsName) handleSubmit();
              }}
            />
          </div>
          {needsName && (
            <div className="space-y-2">
              <Label htmlFor="customer-firstname">Como podemos te chamar?</Label>
              <Input
                id="customer-firstname"
                value={firstName}
                onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
                placeholder="Seu primeiro nome"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              />
            </div>
          )}
          {needsName && askBirthDate && (
            <div className="space-y-2">
              <Label htmlFor="customer-birthdate">Data de nascimento{birthDateRequired ? "" : " (opcional)"}</Label>
              <BirthDateInput
                id="customer-birthdate"
                value={birthDate}
                onChange={setBirthDate}
                onEnter={handleSubmit}
              />
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button className="w-full" onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitting && demorou ? "Enviando…" : needsName ? "Continuar" : "Entrar"}
          </Button>
          {submitting && demorou && (
            <p className="text-sm text-muted-foreground text-center" data-testid="aviso-codigo-30s">{CODIGO_CHEGA_EM_30S}</p>
          )}
          <p className="text-xs text-muted-foreground text-center">
            Ao continuar você confirma que este WhatsApp é seu.
          </p>
        </div>
      )}
    </ModalShell>
  );
}
